import { useRef, type ReactNode, type Ref, type SVGProps } from 'react';
import { useNoTouchScroll } from '../lib/hooks';

type PointerHandlers = Pick<SVGProps<SVGSVGElement>, 'onPointerDown' | 'onPointerMove' | 'onPointerUp' | 'onPointerCancel'>;

interface Props extends PointerHandlers {
  plan: { width: number; height: number };
  image: string;
  svgRef: Ref<SVGSVGElement>;
  /** A crosshair while a tap or stroke would place something. */
  crosshair: boolean;
  label: string;
  children: ReactNode;
}

/**
 * The site plan's image with a drawing layer over it in plan pixels (its
 * viewBox is the plan's size), sized to the screen's width: the photo pins
 * (site-plan-view.tsx) and the access lines (access-plan-view.tsx) are drawn in it.
 */
export function PlanOverlay({ plan, image, svgRef, crosshair, label, children, ...handlers }: Props) {
  const frame = useRef<HTMLDivElement>(null);
  useNoTouchScroll(frame);
  return (
    <div ref={frame} className="relative w-full touch-none overflow-hidden rounded-lg bg-slate-200" style={{ aspectRatio: `${plan.width} / ${plan.height}` }}>
      <img src={image} alt="Site plan" className="absolute inset-0 h-full w-full max-w-none select-none" draggable={false} />
      <svg ref={svgRef} viewBox={`0 0 ${plan.width} ${plan.height}`} className={`absolute inset-0 h-full w-full touch-none ${crosshair ? 'cursor-crosshair' : ''}`} role="img" aria-label={label} {...handlers}>
        {children}
      </svg>
    </div>
  );
}
